/** Tela de entrada: escolha da loja + credenciais. */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Api } from '../api/endpoints';
import { ApiSenha, type RespostaEsqueci } from '../api/senha';
import { mensagemDeErro } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Icone } from '../components/Icone';
import { Brasao, useMarcaDaLoja } from '../components/Marca';
import { subdominioAtual } from '../utils/dominio';
import { SeletorDeLoja } from '../components/SeletorDeLoja';
import { TelaDeAcesso } from '../components/TelaDeAcesso';

/** Última loja em que se entrou neste navegador (só o slug, nada da sessão). */
const CHAVE_ULTIMA_LOJA = 'portal.ultima-loja';

export function Login() {
  const { entrar, usuario } = useAuth();
  const navegar = useNavigate();
  const [params] = useSearchParams();

  // A mesma caixa serve às três etapas: entrar, pedir o link e "enviamos".
  // Trocar de tela no meio de um esquecimento desorienta mais do que ajuda.
  const [modo, setModo] = useState<'entrar' | 'esqueci' | 'enviado'>(
    params.get('esqueci') ? 'esqueci' : 'entrar',
  );
  const [enviado, setEnviado] = useState<RespostaEsqueci | null>(null);
  const [aguardar, setAguardar] = useState(0);

  // `?loja=` fixa a loja e esconde o seletor. É assim que se entra no espaço
  // da plataforma, que de propósito não aparece na lista de lojas.
  // Pelo subdomínio da loja (whitelabel) a escolha também é automática: a
  // loja vem de `/lojas/por-subdominio`, público, antes de qualquer sessão.
  const subdominio = subdominioAtual();
  const {
    data: lojaDoSubdominio,
    isError: subdominioDesconhecido,
  } = useQuery({
    queryKey: ['loja-subdominio', subdominio],
    queryFn: () => Api.lojaPorSubdominio(subdominio!),
    enabled: subdominio !== null,
    retry: false,
    staleTime: Infinity,
  });
  const lojaFixa = params.get('loja') ?? lojaDoSubdominio?.slug ?? null;
  const aguardandoSubdominio = subdominio !== null && !lojaDoSubdominio && !subdominioDesconhecido;
  const [loja, setLoja] = useState(lojaFixa ?? '');
  useEffect(() => {
    if (lojaDoSubdominio) setLoja(lojaDoSubdominio.slug);
  }, [lojaDoSubdominio]);
  // Convite por e-mail: o link traz usuário e senha no `#`, que o navegador
  // não manda ao servidor. Os campos já abrem preenchidos — o irmão só toca em
  // Entrar — e o `#` sai da barra de endereço logo em seguida.
  const [convite] = useState(lerConvite);
  useEffect(() => {
    if (convite) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
  }, [convite]);
  const [login, setLogin] = useState(convite?.usuario ?? '');
  const [senha, setSenha] = useState(convite?.senha ?? '');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  const {
    data: lojas,
    isError: lojasFalharam,
    refetch: recarregarLojas,
  } = useQuery({ queryKey: ['lojas'], queryFn: Api.lojas });

  // Sem a lista de lojas não dá para entrar nem pedir senha. Dizer "selecione
  // a loja" com o seletor vazio mandaria a pessoa procurar algo que não existe.
  const semLoja = () => {
    if (lojasFalharam || !lojas?.length) {
      recarregarLojas();
      return setErro('Não foi possível falar com o servidor. Tente de novo em instantes.');
    }
    return setErro('Selecione a loja.');
  };

  // Com uma loja só, não faz sentido obrigar a escolher. Mas nunca enquanto o
  // subdomínio ainda está sendo resolvido (ou já resolveu): as duas respostas
  // chegam no mesmo instante e este efeito, vendo `loja` vazia, sobrescrevia a
  // loja do whitelabel pela primeira da lista.
  // Sem loja fixa, vale a última em que se entrou neste navegador — quem sai e
  // volta não deve cair na primeira da lista e ter de escolher de novo.
  useEffect(() => {
    if (lojaFixa !== null || aguardandoSubdominio) return;
    if (!lojas?.length || loja) return;
    let ultima: string | null = null;
    try {
      ultima = localStorage.getItem(CHAVE_ULTIMA_LOJA);
    } catch {
      ultima = null;
    }
    setLoja(lojas.some((l) => l.slug === ultima) ? (ultima as string) : lojas[0].slug);
  }, [lojas, loja, lojaFixa, aguardandoSubdominio]);

  useEffect(() => {
    if (usuario) navegar('/', { replace: true });
  }, [usuario, navegar]);

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setErro('');
    if (!loja) return semLoja();
    if (!login.trim()) return setErro('Informe o usuário.');
    if (!senha) return setErro('Digite a senha.');

    setEnviando(true);
    try {
      await entrar(loja, login.trim(), senha);
      try {
        localStorage.setItem(CHAVE_ULTIMA_LOJA, loja);
      } catch {
        // Navegador sem armazenamento: só não lembra a loja da próxima vez.
      }
      navegar('/', { replace: true });
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não foi possível entrar.'));
      setSenha('');
    } finally {
      setEnviando(false);
    }
  };

  // Reenvio só depois de um minuto: o e-mail costuma levar alguns segundos, e
  // apertar de novo na hora gera dois links, dos quais só o último vale.
  useEffect(() => {
    if (aguardar <= 0) return;
    const t = setTimeout(() => setAguardar((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [aguardar]);

  const pedirLink = async (evento?: FormEvent) => {
    evento?.preventDefault();
    setErro('');
    if (!loja) return semLoja();
    if (!login.trim()) return setErro('Informe seu CIM ou o e-mail do cadastro.');
    setEnviando(true);
    try {
      setEnviado(await ApiSenha.esqueci(loja, login.trim()));
      setModo('enviado');
      setAguardar(60);
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não foi possível enviar o link.'));
    } finally {
      setEnviando(false);
    }
  };

  const irPara = (destino: 'entrar' | 'esqueci') => {
    setErro('');
    setSenha('');
    setModo(destino);
  };

  const selecionada = lojas?.find((l) => l.slug === loja);
  const naPlataforma = lojaFixa !== null && !selecionada;
  // A aba já mostra o nome e o brasão de quem está sendo acessado, antes de
  // existir sessão: a loja vem de `/lojas`, que é público.
  useMarcaDaLoja(selecionada);

  return (
    <TelaDeAcesso>
      <form className="login-box" onSubmit={modo === 'esqueci' ? pedirLink : enviar}>
        {/* Entrando, a caixa é da loja e mostra o brasão dela. Nas etapas de
            senha, o assunto é a chave e o e-mail — não a loja. */}
        {modo === 'entrar' ? (
          <Brasao loja={selecionada} caixa="login-logo" tamanho={28} />
        ) : (
          <div className="login-logo">
            <Icone nome={modo === 'esqueci' ? 'chave' : 'email'} tamanho={28} />
          </div>
        )}
        <div className="login-title">
          {modo === 'entrar'
            ? (selecionada?.nome ?? (naPlataforma ? 'Administração da plataforma' : 'Entrar'))
            : modo === 'esqueci'
              ? 'Esqueci minha senha'
              : 'Verifique seu e-mail'}
        </div>
        <div className="login-sub">
          {modo === 'entrar' && selecionada && (
            <>
              {[
                // O nome cadastrado às vezes já traz o número ("… nº 1694").
                selecionada.numero && !selecionada.nome.includes(selecionada.numero)
                  ? `Loja nº ${selecionada.numero}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </>
          )}
          {modo === 'esqueci' &&
            'Informe seu CIM ou o e-mail do cadastro. Enviaremos um link para você criar uma nova senha.'}
        </div>

        {modo === 'enviado' && enviado ? (
          <div className="login-enviado">
            <p>Enviamos um link para</p>
            <div className="login-email-destino">{enviado.email_mascarado}</div>
            <p>
              Ele vale por{' '}
              <strong>
                {enviado.validade_minutos % 60 === 0
                  ? `${enviado.validade_minutos / 60} hora${enviado.validade_minutos > 60 ? 's' : ''}`
                  : `${enviado.validade_minutos} minutos`}
              </strong>{' '}
              e funciona uma única vez.
            </p>
            <div className="login-dica">
              Não chegou em alguns minutos? Confira a caixa de <strong>spam</strong> ou{' '}
              <strong>lixo eletrônico</strong>. Se o endereço acima não for o seu, procure a
              Secretaria para atualizar o cadastro.
            </div>
            <button
              type="button"
              className="login-btn secundario"
              onClick={() => pedirLink()}
              disabled={enviando || aguardar > 0}
            >
              {enviando ? 'ENVIANDO…' : aguardar > 0 ? `REENVIAR EM ${aguardar}s` : 'REENVIAR O LINK'}
            </button>
          </div>
        ) : (
          <>
            {subdominioDesconhecido && (
              <div className="login-dica">Nenhuma loja atende em <strong>{window.location.hostname}</strong>. Escolha a sua abaixo.</div>
            )}
            {!lojaFixa && !aguardandoSubdominio && lojas && lojas.length > 1 && (
              <>
                <div className="login-separator">Loja</div>
                <SeletorDeLoja
                  lojas={lojas}
                  valor={loja}
                  aoEscolher={setLoja}
                  desabilitado={enviando}
                />
              </>
            )}

            <div className="login-separator">{modo === 'entrar' ? 'Credenciais' : 'Identificação'}</div>
            {/* O irmão entra com o que já sabe de cor: o CIM ou o e-mail do
                cadastro. Um "nome de usuário" à parte seria uma terceira
                identidade para a mesma pessoa. */}
            <input
              className="login-input"
              type="text"
              placeholder="CIM ou e-mail"
              aria-label="CIM ou e-mail do cadastro"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              disabled={enviando}
              autoFocus={modo === 'esqueci'}
            />
            {modo === 'entrar' && (
              <div className="login-senha">
                <input
                  className="login-input"
                  type={mostrarSenha ? 'text' : 'password'}
                  placeholder="Senha"
                  aria-label="Senha"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  autoComplete="current-password"
                  disabled={enviando}
                />
                <button
                  type="button"
                  className="login-olho"
                  onClick={() => setMostrarSenha((v) => !v)}
                  aria-label={mostrarSenha ? 'Esconder senha' : 'Mostrar senha'}
                  title={mostrarSenha ? 'Esconder senha' : 'Mostrar senha'}
                >
                  <Icone nome={mostrarSenha ? 'olhoFechado' : 'olho'} tamanho={17} />
                </button>
              </div>
            )}

            {modo === 'entrar' && convite && (
              <p className="login-convite">Seu usuário e sua senha já estão preenchidos. É só tocar em Entrar.</p>
            )}
            <button className="login-btn" type="submit" disabled={enviando} autoFocus={modo === 'entrar' && !!convite}>
              {modo === 'entrar'
                ? enviando
                  ? 'ENTRANDO…'
                  : 'ENTRAR'
                : enviando
                  ? 'ENVIANDO…'
                  : 'ENVIAR LINK POR E-MAIL'}
            </button>
          </>
        )}

        <div className="login-err" role="alert">
          {erro}
        </div>

        <div className="login-links">
          {modo === 'entrar' ? (
            <button type="button" className="login-link" onClick={() => irPara('esqueci')}>
              Esqueci minha senha
            </button>
          ) : (
            <button type="button" className="login-link" onClick={() => irPara('entrar')}>
              ← Voltar para o login
            </button>
          )}
        </div>

        {/* LGPD: quem entra precisa poder ler antes o que se faz com os dados. */}
        <div className="login-links">
          <Link to="/termos" className="login-link">
            Termos de uso
          </Link>
          <Link to="/privacidade" className="login-link">
            Privacidade
          </Link>
        </div>
      </form>
    </TelaDeAcesso>
  );
}

/** `#u=<usuário>&s=<senha>` do e-mail de convite, ou nada. */
function lerConvite(): { usuario: string; senha: string } | null {
  const dados = new URLSearchParams(window.location.hash.slice(1));
  const usuario = dados.get('u');
  const senha = dados.get('s');
  return usuario && senha ? { usuario, senha } : null;
}
